import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
} from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Badge } from './Badge';

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  paramName: string;
  defaultValue: string;
  value?: string;
  onValueChange?: (next: string) => void;
  allowedValues?: string[];
  variant?: 'line' | 'enclosed' | 'pill';
  children: ReactNode;
}

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
  'aria-label'?: string;
  children: ReactNode;
}

export interface TabsTriggerProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
  leadingIcon?: ReactNode;
  trailingCount?: number | string;
  children: ReactNode;
}

export interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  forceMount?: boolean;
  children: ReactNode;
}

interface TabsContextValue {
  baseId: string;
  activeValue: string;
  activate: (next: string) => void;
  variant: 'line' | 'enclosed' | 'pill';
}

const TabsContext = createContext<TabsContextValue | null>(null);

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

function getTabId(baseId: string, value: string): string {
  return `${baseId}-tab-${value}`;
}

function getPanelId(baseId: string, value: string): string {
  return `${baseId}-panel-${value}`;
}

function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('Tabs compound components must be rendered inside <Tabs>.');
  }

  return context;
}

const TabsRoot = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  {
    paramName,
    defaultValue,
    value,
    onValueChange,
    allowedValues,
    variant = 'line',
    className,
    children,
    ...rest
  },
  ref,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const baseId = useId().replace(/:/g, '');
  const controlled = value !== undefined;
  const rawUrlValue = searchParams.get(paramName) ?? defaultValue;
  const isAllowed = !allowedValues || allowedValues.includes(rawUrlValue);
  const urlValue = isAllowed ? rawUrlValue : defaultValue;
  const activeValue = controlled ? value : urlValue;

  useEffect(() => {
    if (controlled) {
      return;
    }

    if (searchParams.get(paramName)) {
      if (isAllowed) {
        return;
      }
    }

    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set(paramName, defaultValue);
      return next;
    }, { replace: true });
  }, [controlled, defaultValue, isAllowed, paramName, searchParams, setSearchParams]);

  const activate = (next: string) => {
    onValueChange?.(next);

    if (controlled) {
      return;
    }

    setSearchParams((previous) => {
      const updated = new URLSearchParams(previous);
      updated.set(paramName, next);
      return updated;
    }, { replace: false });
  };

  const contextValue: TabsContextValue = {
    baseId,
    activeValue,
    activate,
    variant,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <div ref={ref} className={cn('w-full', className)} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
});

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
  { className, children, onKeyDown, ...rest },
  ref,
) {
  const { variant } = useTabsContext();

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) {
      return;
    }

    const allowedKeys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!allowedKeys.includes(event.key)) {
      return;
    }

    const tabElements = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    ).filter((tabElement) => !tabElement.disabled && tabElement.getAttribute('aria-disabled') !== 'true');

    if (tabElements.length === 0) {
      return;
    }

    const focusedIndex = tabElements.findIndex((tabElement) => tabElement === document.activeElement);
    const safeIndex = focusedIndex >= 0 ? focusedIndex : 0;
    let nextIndex = safeIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (safeIndex + 1) % tabElements.length;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (safeIndex - 1 + tabElements.length) % tabElements.length;
    }

    if (event.key === 'Home') {
      nextIndex = 0;
    }

    if (event.key === 'End') {
      nextIndex = tabElements.length - 1;
    }

    tabElements[nextIndex]?.focus();
    event.preventDefault();
  };

  return (
    <div
      ref={ref}
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        'inline-flex items-center',
        variant === 'line' && 'border-b border-[color:var(--color-border-subtle)]',
        variant === 'pill' && 'rounded-full bg-[color:var(--color-surface-subtle)] p-1',
        className,
      )}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </div>
  );
});

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(function TabsTrigger(
  {
    value,
    leadingIcon,
    trailingCount,
    className,
    children,
    disabled,
    onClick,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const { activeValue, activate, baseId, variant } = useTabsContext();
  const isActive = activeValue === value;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    onClick?.(event);

    if (event.defaultPrevented || disabled) {
      return;
    }

    activate(value);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
    onKeyDown?.(event);

    if (event.defaultPrevented || disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate(value);
    }
  };

  const lineClasses = isActive
    ? 'text-[color:var(--color-text-primary)] border-b-2 border-[color:var(--color-focus-ring)]'
    : 'text-[color:var(--color-text-secondary)] border-b-2 border-transparent hover:text-[color:var(--color-text-primary)] hover:bg-[color:var(--color-hover-overlay)]';

  const enclosedClasses = isActive
    ? 'rounded-t-lg border border-[color:var(--color-border)] border-b-0 bg-[color:var(--color-surface)] text-[color:var(--color-text-primary)]'
    : 'rounded-t-lg text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-hover-overlay)]';

  const pillClasses = isActive
    ? 'rounded-full bg-[color:var(--color-selected-bg)] text-[color:var(--color-selected-fg)]'
    : 'rounded-full text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-hover-overlay)]';

  return (
    <button
      ref={ref}
      id={getTabId(baseId, value)}
      role="tab"
      type="button"
      aria-selected={isActive}
      aria-controls={getPanelId(baseId, value)}
      tabIndex={isActive ? 0 : -1}
      aria-disabled={disabled ? true : undefined}
      disabled={disabled}
      className={cn(
        'inline-flex h-10 items-center gap-2 px-3 text-sm font-medium outline-none',
        variant === 'line' && lineClasses,
        variant === 'enclosed' && enclosedClasses,
        variant === 'pill' && pillClasses,
        'focus-visible:ring-2 focus-visible:ring-[color:var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-focus-ring-offset)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {leadingIcon}
      <span>{children}</span>
      {trailingCount !== undefined ? (
        <Badge size="sm" variant="neutral">{trailingCount}</Badge>
      ) : null}
    </button>
  );
});

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(function TabsContent(
  { value, forceMount = false, className, children, ...rest },
  ref,
) {
  const { activeValue, baseId } = useTabsContext();
  const isActive = activeValue === value;

  if (!isActive && !forceMount) {
    return null;
  }

  return (
    <div
      ref={ref}
      id={getPanelId(baseId, value)}
      role="tabpanel"
      aria-labelledby={getTabId(baseId, value)}
      tabIndex={0}
      hidden={!isActive}
      className={cn('mt-3 outline-none', className)}
      {...rest}
    >
      {children}
    </div>
  );
});

export const Tabs = Object.assign(TabsRoot, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
});
